import type { FunctionComponent, ReactElement } from 'react';

import { usePermissions } from '../../../../hooks/api';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import Redirect from '../../../generic/Redirect';
import { NetworkError } from '../../Errors';

/**
 * Route gate for the Organization Users pages. The settings nav already hides these pages from
 * users without `UserAny:View`, but the routes themselves are reachable by URL, so gate them here
 * too. Anyone without the permission is sent to Teams, which every user is able to view.
 */
export const OrganizationUsersGate: FunctionComponent = ({ children }) => {
  const { isLoading, error } = usePermissions();
  const hasPermission = useHasPermission();

  /*
   * Permissions are fetched, so until they land every check reads as `false`. Render nothing
   * rather than redirecting a user who does in fact have access.
   */
  if (isLoading) {
    return null;
  }

  /*
   * A failed request tells us nothing about the user's access, so surface the failure instead of
   * quietly bouncing someone who may well have permission.
   */
  if (error) {
    return <NetworkError />;
  }

  if (!hasPermission({ resourceType: ResourceType.UserAny, action: Action.View })) {
    return <Redirect replace to="/account/settings/organization/teams" />;
  }

  return children as ReactElement; // Required to avoid errors in the consuming component.
};
